import React, { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { auth } from "../lib/api";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { GAMES, FORMATS, getGameColor, getGameName } from "../constants/theme";
import {
  User, Mail, Save, Shield, Phone, MapPin, Eye, EyeOff, Users,
  Plus, Trash2, Search, X, Instagram, Globe, Youtube, MessageCircle,
  Store, Upload, AlertTriangle, Check, Loader2, ChevronDown, ChevronUp,
  Link2, AtSign, CalendarDays, Bell,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";

const PRIVACY_OPTIONS = [
  { value: "public", label: "Public", icon: Eye },
  { value: "friends", label: "Amis uniquement", icon: Users },
  { value: "private", label: "Moi", icon: EyeOff },
];

function PrivacySelect({ value, onChange, testId }) {
  return (
    <div className="flex gap-1">
      {PRIVACY_OPTIONS.map(opt => (
        <button key={opt.value} type="button" onClick={() => onChange(opt.value)}
          data-testid={`${testId}-${opt.value}`}
          className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors ${
            value === opt.value ? "bg-[#FF6B35] text-white" : "bg-[#1f1f1f] text-[#888] hover:bg-[#2a2a2a]"
          }`}>
          <opt.icon className="w-3 h-3" />
          {opt.label}
        </button>
      ))}
    </div>
  );
}

function CityAutocomplete({ value, onChange, onSelect }) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const timerRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (value.length < 2) { setSuggestions([]); return; }
    setLoading(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`${API}/api/geocode?q=${encodeURIComponent(value)}&limit=6`);
        const data = await res.json();
        setSuggestions(data || []);
        setOpen(true);
      } catch { setSuggestions([]); }
      finally { setLoading(false); }
    }, 300);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [value]);

  useEffect(() => {
    const handleClick = (e) => { if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[#555]" />
        <Input value={value} onChange={e => { onChange(e.target.value); setOpen(true); }}
          onFocus={() => suggestions.length > 0 && setOpen(true)}
          placeholder="Rechercher une ville ou adresse..."
          className="bg-[#0a0a0a] border-[#333] text-white pl-9 text-sm" data-testid="city-input" />
        {loading && <div className="absolute right-3 top-1/2 -translate-y-1/2"><div className="w-4 h-4 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" /></div>}
      </div>
      {open && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-[#1a1a1a] border border-[#333] rounded-lg shadow-xl max-h-48 overflow-y-auto">
          {suggestions.map((s, i) => (
            <button key={i} className="w-full flex items-center gap-2.5 px-3 py-2.5 hover:bg-[#252525] transition-colors text-left border-b border-[#333]/50 last:border-0"
              onClick={() => { onSelect(s); setOpen(false); setSuggestions([]); }}>
              <MapPin className="w-4 h-4 text-[#4ECDC4] flex-shrink-0" />
              <div className="min-w-0">
                <div className="text-white text-sm font-medium truncate">{s.name || s.display_name?.split(",")[0]}</div>
                <div className="text-[#666] text-xs truncate">{s.display_name}</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ShopBlock({ shop, index, onUpdate, onRemove, onUpload }) {
  const set = (k, v) => onUpdate(index, { ...shop, [k]: v });

  const handleFileUpload = async (field, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await api.post("/upload/shop-asset", formData, { headers: { "Content-Type": "multipart/form-data" } });
      set(field, res.data.url);
      toast.success(`${field === "logo_url" ? "Logo" : "Banniere"} uploade !`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur upload");
    }
  };

  return (
    <div className="bg-[#0a0a0a] rounded-lg p-4 border border-[#333] space-y-3" data-testid={`shop-block-${index}`}>
      <div className="flex items-center justify-between">
        <h4 className="text-white font-semibold text-sm">Boutique {index + 1}</h4>
        <button onClick={() => onRemove(index)} className="p-1 rounded text-[#666] hover:text-red-400 hover:bg-red-400/10 transition-colors" data-testid={`remove-shop-${index}`}>
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-[#888] text-xs">Nom de la boutique</Label>
          <Input value={shop.name || ""} onChange={e => set("name", e.target.value)}
            className="bg-[#1a1a1a] border-[#333] text-white text-sm" data-testid={`shop-name-${index}`} />
        </div>
        <div className="space-y-1">
          <Label className="text-[#888] text-xs">Site web</Label>
          <Input value={shop.website || ""} onChange={e => set("website", e.target.value)}
            placeholder="https://..." className="bg-[#1a1a1a] border-[#333] text-white text-sm" />
        </div>
      </div>
      <div className="space-y-1">
        <Label className="text-[#888] text-xs">Adresse de la boutique</Label>
        <Input value={shop.address || ""} onChange={e => set("address", e.target.value)}
          className="bg-[#1a1a1a] border-[#333] text-white text-sm" data-testid={`shop-address-${index}`} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-[#888] text-xs">Logo</Label>
          <div className="flex items-center gap-2">
            {shop.logo_url && <img src={shop.logo_url.startsWith("http") ? shop.logo_url : `${API}${shop.logo_url}`} alt="" className="w-10 h-10 rounded-full object-cover border border-[#333]" />}
            <label className="flex items-center gap-1.5 px-3 py-1.5 bg-[#1a1a1a] border border-[#333] rounded-lg text-[#B0B0B0] text-xs cursor-pointer hover:border-[#FF6B35] transition-colors">
              <Upload className="w-3.5 h-3.5" /> Upload
              <input type="file" accept="image/*" className="hidden" onChange={e => handleFileUpload("logo_url", e)} />
            </label>
          </div>
        </div>
        <div className="space-y-1">
          <Label className="text-[#888] text-xs">Banniere</Label>
          <div className="flex items-center gap-2">
            {shop.banner_url && <img src={shop.banner_url.startsWith("http") ? shop.banner_url : `${API}${shop.banner_url}`} alt="" className="w-16 h-10 rounded object-cover border border-[#333]" />}
            <label className="flex items-center gap-1.5 px-3 py-1.5 bg-[#1a1a1a] border border-[#333] rounded-lg text-[#B0B0B0] text-xs cursor-pointer hover:border-[#FF6B35] transition-colors">
              <Upload className="w-3.5 h-3.5" /> Upload
              <input type="file" accept="image/*" className="hidden" onChange={e => handleFileUpload("banner_url", e)} />
            </label>
          </div>
        </div>
      </div>
      <div className="space-y-1">
        <Label className="text-[#888] text-xs">Conditions de jeu libre et tarif</Label>
        <textarea value={shop.play_conditions || ""} onChange={e => set("play_conditions", e.target.value)}
          placeholder="Ex: Jeu libre gratuit du lundi au vendredi. 2 euros le samedi..."
          className="w-full px-3 py-2 rounded-md bg-[#1a1a1a] border border-[#333] text-white text-sm resize-none h-16 focus:outline-none focus:border-[#FF6B35]" />
      </div>
    </div>
  );
}

function UnsavedModal({ onSave, onDiscard }) {
  return (
    <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="unsaved-modal">
      <div className="bg-[#1a1a1a] border border-[#333] rounded-2xl p-6 w-full max-w-sm text-center">
        <AlertTriangle className="w-10 h-10 text-[#FF6B35] mx-auto mb-3" />
        <h3 className="text-white font-bold text-lg mb-1">Modifications non sauvegardees</h3>
        <p className="text-[#888] text-sm mb-5">Tu n'as pas sauvegarde ton profil</p>
        <div className="flex gap-3">
          <Button variant="outline" onClick={onDiscard} className="flex-1 border-[#333] text-[#B0B0B0] hover:text-white" data-testid="discard-btn">
            Quitter la page
          </Button>
          <Button onClick={onSave} className="flex-1 bg-[#FF6B35] hover:bg-[#E55A25]" data-testid="save-and-stay-btn">
            <Save className="w-4 h-4 mr-1" /> Sauvegarder
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function PlayerProfilePage() {
  const { user, updateUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [pseudoStatus, setPseudoStatus] = useState(null); // null | "checking" | "available" | "taken"
  const [showUnsaved, setShowUnsaved] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [gameFormats, setGameFormats] = useState({});
  const unsavedShownRef = useRef(false);
  const [expandedGames, setExpandedGames] = useState({});

  const defaultPrivacy = user?.privacy_settings || { email: "public", phone: "private", city: "public", events: "public" };
  const PE_NOTIF_TYPES = [
    { key: "player_event_interest", label: "Nouvel intéressé à ma partie" },
    { key: "player_event_decision", label: "Ma demande confirmée / refusée" },
    { key: "player_event_message", label: "Nouveau message sur une partie" },
    { key: "player_event_result", label: "Résultat d'une partie enregistré" },
  ];
  const [notifPrefs, setNotifPrefs] = useState(user?.notification_preferences || {});
  const setNotifPref = (key, val) => { setNotifPrefs((p) => ({ ...p, [key]: val })); setHasChanges(true); };

  const [form, setForm] = useState({
    pseudo: user?.pseudo || "",
    name: user?.name || "",
    bio: user?.bio || "",
    avatar_url: user?.avatar_url || "",
    instagram: user?.instagram || "",
    facebook: user?.facebook || "",
    youtube: user?.youtube || "",
    moxfield: user?.moxfield || "",
    archidekt: user?.archidekt || "",
    discord_group_link: user?.discord_group_link || "",
    discord_pseudo: user?.discord_pseudo || "",
    contact_email: user?.contact_email || "",
    phone: user?.phone || "",
    city: user?.city || "",
    city_lat: user?.city_lat || null,
    city_lng: user?.city_lng || null,
    games_played: user?.games_played || [],
    formats_played: user?.formats_played || [],
  });
  const [privacy, setPrivacy] = useState(defaultPrivacy);
  const [shops, setShops] = useState(user?.shops || []);
  const [cityInput, setCityInput] = useState(user?.city || "");

  // Fetch game formats from DB
  useEffect(() => {
    api.get("/game-formats").then(r => {
      const byGame = {};
      r.data.forEach(f => {
        if (!byGame[f.game]) byGame[f.game] = [];
        byGame[f.game].push(f);
      });
      setGameFormats(byGame);
    }).catch(() => {});
  }, []);

  // Track changes
  useEffect(() => {
    setHasChanges(true);
  }, [form, privacy, shops]);

  // Warn on page leave
  useEffect(() => {
    const handler = (e) => {
      if (hasChanges) { e.preventDefault(); e.returnValue = ""; }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasChanges]);

  // Pseudo check with debounce
  const pseudoTimer = useRef(null);
  useEffect(() => {
    if (!form.pseudo || form.pseudo.length < 2) { setPseudoStatus(null); return; }
    if (form.pseudo === user?.pseudo) { setPseudoStatus("available"); return; }
    setPseudoStatus("checking");
    if (pseudoTimer.current) clearTimeout(pseudoTimer.current);
    pseudoTimer.current = setTimeout(async () => {
      try {
        const res = await fetch(`${API}/api/auth/check-pseudo?pseudo=${encodeURIComponent(form.pseudo)}`);
        const data = await res.json();
        setPseudoStatus(data.available ? "available" : "taken");
      } catch { setPseudoStatus(null); }
    }, 500);
    return () => { if (pseudoTimer.current) clearTimeout(pseudoTimer.current); };
  }, [form.pseudo, user?.pseudo]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const updatePrivacy = (field, value) => setPrivacy(p => ({ ...p, [field]: value }));

  const [avatarUploading, setAvatarUploading] = useState(false);
  const [pwd, setPwd] = useState({ current: "", next: "", confirm: "" });
  const [pwdLoading, setPwdLoading] = useState(false);
  const handleChangePassword = async () => {
    if (!pwd.current || !pwd.next) { toast.error("Renseignez le mot de passe actuel et le nouveau"); return; }
    if (pwd.next.length < 8) { toast.error("Le nouveau mot de passe doit faire au moins 8 caractères"); return; }
    if (pwd.next !== pwd.confirm) { toast.error("La confirmation ne correspond pas"); return; }
    setPwdLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: pwd.current, new_password: pwd.next });
      toast.success("Mot de passe modifié avec succès");
      setPwd({ current: "", next: "", confirm: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors du changement de mot de passe");
    } finally {
      setPwdLoading(false);
    }
  };
  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await api.post("/upload/shop-asset", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("avatar_url", res.data.url);
      toast.success("Photo de profil mise à jour ! N'oubliez pas de sauvegarder.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur upload");
    } finally {
      setAvatarUploading(false);
      e.target.value = "";
    }
  };

  const toggleGame = (gameId) => {
    setForm(f => {
      const gp = f.games_played.includes(gameId)
        ? f.games_played.filter(g => g !== gameId)
        : [...f.games_played, gameId];
      const fp = f.games_played.includes(gameId)
        ? f.formats_played.filter(fmt => !fmt.startsWith(gameId + ":"))
        : f.formats_played;
      return { ...f, games_played: gp, formats_played: fp };
    });
  };

  const toggleFormat = (gameId, formatName) => {
    const key = `${gameId}:${formatName}`;
    setForm(f => ({
      ...f,
      formats_played: f.formats_played.includes(key)
        ? f.formats_played.filter(x => x !== key)
        : [...f.formats_played, key],
    }));
  };

  const handleCitySelect = (city) => {
    const name = city.name || city.display_name?.split(",")[0] || "";
    setCityInput(name);
    set("city", city.display_name || name);
    set("city_lat", parseFloat(city.lat));
    set("city_lng", parseFloat(city.lon));
  };

  const addShop = () => {
    setShops(s => [...s, { id: crypto.randomUUID(), name: "", address: "", website: "", logo_url: "", banner_url: "", play_conditions: "" }]);
  };

  const updateShop = (idx, shop) => setShops(s => s.map((sh, i) => i === idx ? shop : sh));
  const removeShop = (idx) => setShops(s => s.filter((_, i) => i !== idx));

  const handleSave = async () => {
    if (pseudoStatus === "taken") {
      toast.error("Ce pseudo est deja utilise !");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form, privacy_settings: privacy, notification_preferences: notifPrefs, shops };
      const res = await auth.updateProfile(payload);
      updateUser(res.data);
      setHasChanges(false);
      unsavedShownRef.current = false;
      setShowUnsaved(false);
      toast.success("Profil mis a jour !");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  const isShop = user?.role === "ORG" || user?.role === "ADMIN";

  // Get formats for a game: merge DB formats + hardcoded FORMATS
  const getFormatsForGame = (gameId) => {
    const dbFormats = (gameFormats[gameId] || []).map(f => f.name);
    const hardcoded = FORMATS[gameId] || [];
    const all = [...new Set([...hardcoded, ...dbFormats])];
    return all;
  };

  return (
    <div className="max-w-2xl mx-auto pb-8" data-testid="player-profile">
      {showUnsaved && <UnsavedModal onSave={handleSave} onDiscard={() => { setShowUnsaved(false); setHasChanges(false); }} />}

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Mon Profil</h1>
          <p className="text-[#666] text-sm">Gerez vos informations</p>
        </div>
        <Button onClick={handleSave} disabled={loading} className="bg-[#FF6B35] hover:bg-[#E55A25] gap-2" data-testid="save-profile-top">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {loading ? "..." : "Sauvegarder"}
        </Button>
      </div>

      {/* === IDENTITY === */}
      <Card className="bg-[#1a1a1a] border-[#333] mb-4">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center gap-2 pb-3 border-b border-[#333]">
            <User className="w-5 h-5 text-[#FF6B35]" />
            <h2 className="text-white font-bold">Identite</h2>
          </div>

          {/* Photo de profil */}
          <div className="flex items-center gap-4" data-testid="avatar-upload-section">
            <div className="w-20 h-20 rounded-full overflow-hidden bg-[#0a0a0a] border-2 border-[#333] shrink-0 flex items-center justify-center">
              {form.avatar_url
                ? <img src={form.avatar_url.startsWith("http") ? form.avatar_url : `${API}${form.avatar_url}`} alt="" className="w-full h-full object-cover" />
                : <User className="w-8 h-8 text-[#555]" />}
            </div>
            <div className="space-y-1.5">
              <Label className="text-[#B0B0B0] text-sm font-semibold">Photo de profil</Label>
              <div className="flex items-center gap-2">
                <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-[#252525] hover:bg-[#333] text-white text-sm cursor-pointer transition-colors" data-testid="avatar-upload-btn">
                  {avatarUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {avatarUploading ? "Envoi..." : (form.avatar_url ? "Changer" : "Ajouter")}
                  <input type="file" accept="image/*" className="hidden" onChange={handleAvatarUpload} disabled={avatarUploading} data-testid="avatar-file-input" />
                </label>
                {form.avatar_url && (
                  <button onClick={() => set("avatar_url", "")} className="text-[#888] hover:text-red-400 text-sm" data-testid="avatar-remove-btn">Retirer</button>
                )}
              </div>
              <p className="text-[#666] text-xs">JPG, PNG ou WebP · compressée automatiquement</p>
            </div>
          </div>

          {/* Pseudo */}
          <div className="space-y-1">
            <Label className="text-[#B0B0B0] text-sm font-semibold">Pseudo *</Label>
            <div className="relative">
              <Input value={form.pseudo} onChange={e => set("pseudo", e.target.value.replace(/\s/g, ""))}
                className="bg-[#0a0a0a] border-[#333] text-white pr-10" data-testid="profile-pseudo" />
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                {pseudoStatus === "checking" && <Loader2 className="w-4 h-4 text-[#888] animate-spin" />}
                {pseudoStatus === "available" && <Check className="w-4 h-4 text-green-400" />}
                {pseudoStatus === "taken" && <X className="w-4 h-4 text-red-400" />}
              </div>
            </div>
            {pseudoStatus === "taken" && <p className="text-red-400 text-xs">Ce pseudo est deja pris</p>}
          </div>

          {/* Vrai nom */}
          <div className="space-y-1">
            <Label className="text-[#B0B0B0] text-sm">Vrai nom <span className="text-[#666]">(pour les inscriptions aux tournois)</span></Label>
            <Input value={form.name} onChange={e => set("name", e.target.value)}
              className="bg-[#0a0a0a] border-[#333] text-white" data-testid="profile-name" />
          </div>

          {/* Description */}
          <div className="space-y-1">
            <Label className="text-[#B0B0B0] text-sm">Qui es-tu ?</Label>
            <textarea value={form.bio} onChange={e => set("bio", e.target.value)}
              placeholder="Decris-toi en quelques mots..."
              className="w-full px-3 py-2 rounded-md bg-[#0a0a0a] border border-[#333] text-white text-sm resize-none h-20 focus:outline-none focus:border-[#FF6B35]"
              data-testid="profile-bio" />
          </div>
        </CardContent>
      </Card>

      {/* === SOCIAL LINKS === */}
      <Card className="bg-[#1a1a1a] border-[#333] mb-4">
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center gap-2 pb-3 border-b border-[#333]">
            <Link2 className="w-5 h-5 text-[#FF6B35]" />
            <h2 className="text-white font-bold">Liens sociaux</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-[#888] text-xs flex items-center gap-1"><Instagram className="w-3 h-3" /> Instagram</Label>
              <Input value={form.instagram} onChange={e => set("instagram", e.target.value)}
                placeholder="@pseudo" className="bg-[#0a0a0a] border-[#333] text-white text-sm" />
            </div>
            <div className="space-y-1">
              <Label className="text-[#888] text-xs flex items-center gap-1"><Globe className="w-3 h-3" /> Facebook</Label>
              <Input value={form.facebook} onChange={e => set("facebook", e.target.value)}
                placeholder="Lien ou pseudo" className="bg-[#0a0a0a] border-[#333] text-white text-sm" />
            </div>
            <div className="space-y-1">
              <Label className="text-[#888] text-xs flex items-center gap-1"><Youtube className="w-3 h-3" /> YouTube</Label>
              <Input value={form.youtube} onChange={e => set("youtube", e.target.value)}
                placeholder="Lien chaine" className="bg-[#0a0a0a] border-[#333] text-white text-sm" />
            </div>
            <div className="space-y-1">
              <Label className="text-[#888] text-xs flex items-center gap-1"><Globe className="w-3 h-3" /> Moxfield</Label>
              <Input value={form.moxfield} onChange={e => set("moxfield", e.target.value)}
                placeholder="Lien profil" className="bg-[#0a0a0a] border-[#333] text-white text-sm" />
            </div>
            <div className="space-y-1">
              <Label className="text-[#888] text-xs flex items-center gap-1"><Globe className="w-3 h-3" /> Archidekt</Label>
              <Input value={form.archidekt} onChange={e => set("archidekt", e.target.value)}
                placeholder="Lien profil" className="bg-[#0a0a0a] border-[#333] text-white text-sm" />
            </div>
            <div className="space-y-1">
              <Label className="text-[#888] text-xs flex items-center gap-1"><MessageCircle className="w-3 h-3 text-[#5865F2]" /> Groupe Discord</Label>
              <Input value={form.discord_group_link} onChange={e => set("discord_group_link", e.target.value)}
                placeholder="https://discord.gg/..." className="bg-[#0a0a0a] border-[#333] text-white text-sm" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* === CONTACT === */}
      <Card className="bg-[#1a1a1a] border-[#333] mb-4">
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center gap-2 pb-3 border-b border-[#333]">
            <AtSign className="w-5 h-5 text-[#FF6B35]" />
            <h2 className="text-white font-bold">Contact</h2>
          </div>
          <div className="space-y-1">
            <Label className="text-[#888] text-xs flex items-center gap-1"><MessageCircle className="w-3 h-3 text-[#5865F2]" /> Pseudo Discord</Label>
            <Input value={form.discord_pseudo} onChange={e => set("discord_pseudo", e.target.value)}
              placeholder="MonPseudo#1234" className="bg-[#0a0a0a] border-[#333] text-white text-sm" />
          </div>
          <div className="space-y-1">
            <Label className="text-[#888] text-xs flex items-center gap-1"><Mail className="w-3 h-3" /> Email de contact (inscriptions tournois)</Label>
            <Input value={form.contact_email} onChange={e => set("contact_email", e.target.value)}
              placeholder={user?.email || "email@exemple.com"} className="bg-[#0a0a0a] border-[#333] text-white text-sm" data-testid="profile-contact-email" />
          </div>
          <div className="space-y-1">
            <Label className="text-[#888] text-xs flex items-center gap-1"><Phone className="w-3 h-3" /> Telephone</Label>
            <Input value={form.phone} onChange={e => set("phone", e.target.value)}
              placeholder="+33 6 12 34 56 78" className="bg-[#0a0a0a] border-[#333] text-white text-sm" data-testid="profile-phone" />
          </div>
          <div className="space-y-1">
            <Label className="text-[#888] text-xs flex items-center gap-1"><MapPin className="w-3 h-3" /> Ville ou adresse</Label>
            {form.city ? (
              <div className="flex items-center gap-2 bg-[#0a0a0a] rounded-md px-3 py-2 border border-[#333]">
                <MapPin className="w-4 h-4 text-[#4ECDC4]" />
                <span className="text-white text-sm flex-1 truncate">{form.city}</span>
                <button onClick={() => { set("city", ""); set("city_lat", null); set("city_lng", null); setCityInput(""); }}
                  className="text-[#666] hover:text-white"><X className="w-4 h-4" /></button>
              </div>
            ) : (
              <CityAutocomplete value={cityInput} onChange={setCityInput} onSelect={handleCitySelect} />
            )}
          </div>
        </CardContent>
      </Card>

      {/* === PRIVACY === */}
      <Card className="bg-[#1a1a1a] border-[#333] mb-4">
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center gap-2 pb-3 border-b border-[#333]">
            <Shield className="w-5 h-5 text-[#FF6B35]" />
            <h2 className="text-white font-bold">Confidentialite</h2>
          </div>
          <p className="text-[#666] text-xs">Afficher sur le profil public :</p>
          <div className="space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 sm:gap-2">
              <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-[#888]" /><span className="text-[#B0B0B0] text-sm">Email</span></div>
              <PrivacySelect value={privacy.email} onChange={v => updatePrivacy("email", v)} testId="privacy-email" />
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 sm:gap-2">
              <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-[#888]" /><span className="text-[#B0B0B0] text-sm">Telephone</span></div>
              <PrivacySelect value={privacy.phone} onChange={v => updatePrivacy("phone", v)} testId="privacy-phone" />
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 sm:gap-2">
              <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[#888]" /><span className="text-[#B0B0B0] text-sm">Ville</span></div>
              <PrivacySelect value={privacy.city} onChange={v => updatePrivacy("city", v)} testId="privacy-city" />
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 sm:gap-2">
              <div className="flex items-center gap-2"><CalendarDays className="w-4 h-4 text-[#888]" /><span className="text-[#B0B0B0] text-sm">Evenements</span></div>
              <PrivacySelect value={privacy.events} onChange={v => updatePrivacy("events", v)} testId="privacy-events" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* === SÉCURITÉ / MOT DE PASSE === */}
      <Card className="bg-[#1a1a1a] border-[#333] mb-4" data-testid="password-card">
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center gap-2 pb-3 border-b border-[#333]">
            <Shield className="w-5 h-5 text-[#FF6B35]" />
            <h2 className="text-white font-bold">Mot de passe</h2>
          </div>
          <p className="text-[#666] text-xs">Changez le mot de passe de votre compte.</p>
          <div className="space-y-1">
            <Label className="text-[#888] text-xs">Mot de passe actuel</Label>
            <Input type="password" value={pwd.current} onChange={e => setPwd(p => ({ ...p, current: e.target.value }))}
              autoComplete="current-password" placeholder="••••••••" className="bg-[#0a0a0a] border-[#333] text-white text-sm" data-testid="pwd-current" />
          </div>
          <div className="space-y-1">
            <Label className="text-[#888] text-xs">Nouveau mot de passe (min. 8 caractères)</Label>
            <Input type="password" value={pwd.next} onChange={e => setPwd(p => ({ ...p, next: e.target.value }))}
              autoComplete="new-password" placeholder="••••••••" className="bg-[#0a0a0a] border-[#333] text-white text-sm" data-testid="pwd-new" />
          </div>
          <div className="space-y-1">
            <Label className="text-[#888] text-xs">Confirmer le nouveau mot de passe</Label>
            <Input type="password" value={pwd.confirm} onChange={e => setPwd(p => ({ ...p, confirm: e.target.value }))}
              autoComplete="new-password" placeholder="••••••••" className="bg-[#0a0a0a] border-[#333] text-white text-sm" data-testid="pwd-confirm" />
          </div>
          <Button onClick={handleChangePassword} disabled={pwdLoading}
            className="bg-[#252525] hover:bg-[#333] text-white gap-2 w-full sm:w-auto" data-testid="pwd-submit">
            {pwdLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
            Changer le mot de passe
          </Button>
        </CardContent>
      </Card>

      {/* === GAMES & FORMATS === */}
      <Card className="bg-[#1a1a1a] border-[#333] mb-4">
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center gap-2 pb-3 border-b border-[#333]">
            <div className="w-5 h-5 rounded bg-[#FF6B35]/20 flex items-center justify-center"><span className="text-xs font-bold text-[#FF6B35]">G</span></div>
            <h2 className="text-white font-bold">Jeux pratiques</h2>
          </div>
          <div className="space-y-2">
            {GAMES.map(game => {
              const isSelected = form.games_played.includes(game.id);
              const isExpanded = expandedGames[game.id];
              const formats = getFormatsForGame(game.id);
              const selectedFormats = form.formats_played.filter(f => f.startsWith(game.id + ":"));

              return (
                <div key={game.id} className={`rounded-lg border transition-colors ${isSelected ? "border-[#444] bg-[#0a0a0a]" : "border-[#252525]"}`}>
                  <button
                    type="button"
                    onClick={() => toggleGame(game.id)}
                    className="w-full flex items-center gap-3 px-3 py-2.5 text-left"
                    data-testid={`game-${game.id}`}
                  >
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${game.color}20` }}>
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: game.color }} />
                    </div>
                    <span className={`text-sm font-medium flex-1 ${isSelected ? "text-white" : "text-[#888]"}`}>{game.name}</span>
                    {isSelected && <Check className="w-4 h-4" style={{ color: game.color }} />}
                  </button>
                  {isSelected && formats.length > 0 && (
                    <div className="px-3 pb-3">
                      <button type="button" onClick={() => setExpandedGames(eg => ({ ...eg, [game.id]: !eg[game.id] }))}
                        className="flex items-center gap-1 text-xs text-[#888] hover:text-white mb-2 transition-colors">
                        {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                        Formats ({selectedFormats.length}/{formats.length})
                      </button>
                      {isExpanded && (
                        <div className="flex flex-wrap gap-1.5">
                          {formats.map(fmt => {
                            const key = `${game.id}:${fmt}`;
                            const active = form.formats_played.includes(key);
                            return (
                              <button key={fmt} type="button" onClick={() => toggleFormat(game.id, fmt)}
                                className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all ${
                                  active ? "text-white" : "text-[#888] bg-[#1a1a1a] hover:bg-[#252525]"
                                }`}
                                style={active ? { backgroundColor: `${game.color}30`, color: game.color } : {}}
                                data-testid={`format-${game.id}-${fmt}`}
                              >
                                {fmt}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* === SHOP SECTION (ORG / ADMIN only) === */}
      {isShop && (
        <Card className="bg-[#1a1a1a] border-[#333] mb-4">
          <CardContent className="p-5 space-y-3">
            <div className="flex items-center gap-2 pb-3 border-b border-[#333]">
              <Store className="w-5 h-5 text-[#4ECDC4]" />
              <h2 className="text-white font-bold">Boutique</h2>
            </div>
            {shops.length === 0 && (
              <p className="text-[#666] text-sm text-center py-2">Aucune boutique configuree</p>
            )}
            <div className="space-y-3">
              {shops.map((shop, i) => (
                <ShopBlock key={shop.id || i} shop={shop} index={i} onUpdate={updateShop} onRemove={removeShop} />
              ))}
            </div>
            <button onClick={addShop}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg border border-dashed border-[#333] text-[#888] hover:border-[#4ECDC4] hover:text-[#4ECDC4] transition-colors text-sm"
              data-testid="add-shop-btn">
              <Plus className="w-4 h-4" /> Creer une nouvelle boutique
            </button>
          </CardContent>
        </Card>
      )}

      {/* === NOTIFICATIONS PARTIES ENTRE JOUEURS === */}
      <div className="bg-[#1a1a1a] border border-[#333] rounded-xl p-5 space-y-3" data-testid="notif-prefs-section">
        <div className="flex items-center gap-2 pb-2 border-b border-[#333]">
          <Bell className="w-5 h-5 text-[#7B68EE]" />
          <h2 className="text-white font-bold">Notifications — parties entre joueurs</h2>
        </div>
        <p className="text-[#666] text-xs">Choisissez comment être averti pour chaque type d'événement.</p>
        {PE_NOTIF_TYPES.map(({ key, label }) => {
          const val = notifPrefs[key] || "both";
          const opts = [["both", "Tout"], ["push", "Push"], ["in_app", "App"], ["none", "Aucune"]];
          return (
            <div key={key} className="flex items-center justify-between gap-3 flex-wrap" data-testid={`notif-pref-${key}`}>
              <span className="text-[#B0B0B0] text-sm">{label}</span>
              <div className="flex rounded-lg overflow-hidden border border-[#333]">
                {opts.map(([v, lbl]) => (
                  <button key={v} onClick={() => setNotifPref(key, v)}
                    className={`px-2.5 py-1 text-xs transition-colors ${val === v ? "bg-[#7B68EE] text-white" : "bg-[#252525] text-[#888] hover:text-white"}`}
                    data-testid={`notif-pref-${key}-${v}`}>{lbl}</button>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* === SAVE BUTTON === */}
      <Button onClick={handleSave} disabled={loading || pseudoStatus === "taken"} className="w-full bg-[#FF6B35] hover:bg-[#E55A25] h-12 text-base font-semibold" data-testid="save-profile-btn">
        {loading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Save className="w-5 h-5 mr-2" />}
        {loading ? "Enregistrement..." : "Sauvegarder le profil"}
      </Button>
    </div>
  );
}
